// The error classes + classifiers cloud-functions.service.ts's callApiAsync throws/checks.
// Pulled out of that file (rather than declared inline) so this piece stays loadable
// under plain Node — cloud-functions.service.ts itself imports @angular/core and a long
// chain of Angular services, which is NOT node-testable (see the "auth.service.ts can't
// be loaded under node" note in couch-login-retry-gate.test.ts). couch-login-error-classify.ts
// needs CloudFunctionHttpError / isNetworkError and must stay node-testable itself, so it
// imports from here, not from cloud-functions.service.ts. cloud-functions.service.ts
// re-exports everything below so every existing `from 'services/cloud-functions.service'`
// import keeps working unchanged.

import { isTimeoutError } from './timeout-signal';
import { isRetriableHttpError } from './cloud-functions-retry';
import { isTransientFetchError, isFirebaseAuthNetworkError } from './error-patterns';
import { describeError } from '@cf-shared/error-describe';
import { BackendRouteUnresolvedError } from 'util/backend-route-gate';
import { isThirdPartyOrSelfHealingUrl } from 'app/fetch-failure-classifier';

/** Error thrown when a cloud function is called without a required auth token */
export class CloudFunctionAuthError extends Error {
  readonly functionName: string;

  constructor(functionName: string) {
    super(`API ${functionName}: auth token not available — user is not logged in`);
    this.name = 'CloudFunctionAuthError';
    this.functionName = functionName;
  }
}

/** Error thrown when a cloud function fails due to network issues after retry */
export class CloudFunctionNetworkError extends Error {
  readonly functionName: string;
  readonly originalError: unknown;

  constructor(functionName: string, originalError: unknown) {
    const message = describeError(originalError);
    super(`Network error calling ${functionName}: ${message}`);
    this.name = 'CloudFunctionNetworkError';
    this.functionName = functionName;
    this.originalError = originalError;
  }
}

/** Error thrown when a cloud function returns a non-2xx HTTP response.
 *  Carries `httpStatus` so the retry layer can decide whether to retry
 *  (502/503 — see `isRetriableHttpError`) and so callers can branch on
 *  the specific status. The error is re-thrown to the caller after
 *  retries exhaust; unlike network errors, it is NOT re-wrapped in
 *  `CloudFunctionNetworkError` (the status code matters to callers). */
export class CloudFunctionHttpError extends Error {
  readonly functionName: string;
  readonly httpStatus: number;

  constructor(functionName: string, httpStatus: number) {
    super(`API ${functionName}: HTTP ${httpStatus}`);
    this.name = 'CloudFunctionHttpError';
    this.functionName = functionName;
    this.httpStatus = httpStatus;
  }
}

/** Whether the server refused the call because this caller made too many in the last hour
 *  (HTTP 429, #7027). Not retried: trying again at once can only be refused again. */
export function isRateLimitedError(error: unknown): boolean {
  return error instanceof CloudFunctionHttpError && error.httpStatus === 429;
}

/** fetch() throws TypeError on network failure (DNS, connection refused, etc.)
 * and DOMException with name 'TimeoutError' when AbortSignal.timeout fires.
 * CapacitorHttp on Android can throw plain objects {message, name} that aren't Error instances,
 * and native network-layer failures arrive as plain `Error`s with OS socket-layer messages
 * ("Unable to resolve host …", "Software caused connection abort") — the shared
 * `isTransientFetchError` family (error-patterns.ts) recognizes those shapes, so this
 * matcher delegates to it instead of keeping a second, drifting message list (#4010
 * hygiene: before that delegation, an Android DNS failure during couch login was not
 * retried here and escaped to Sentry mislabeled as an auth failure, APP-3WY/APP-3Z9).
 * `callApiAsync` wraps any of the above as `CloudFunctionNetworkError` once they
 * cross the resilientRetry boundary, so callers that consume the wrapped error
 * (e.g. `checkImportedExerciseForUpdateAsync`) need this matcher to return true
 * for the wrapper too. */
export function isNetworkError(error: unknown): boolean {
  // if the error is a fetch TypeError or a timeout, treat it as a network failure
  // (isTransientFetchError deliberately excludes raw timeouts, so keep this arm)
  if (error instanceof TypeError || isTimeoutError(error)) return true;
  if (error instanceof CloudFunctionNetworkError) return true;
  // #4405: the native backend-route probe never answered in time. The only
  // reason that happens is that the device could not reach the network on a
  // first-ever boot, so this genuinely IS a connectivity failure, not a
  // separate condition. Classifying it here is what makes the app's existing
  // behaviour correct for free: the wrapper retries it, couch-login treats it
  // as expected rather than broken, and it raises no Sentry noise.
  if (error instanceof BackendRouteUnresolvedError) return true;
  // #6119: callApiAsync refreshes the Firebase sign-in token before the fetch. offline, that
  // refresh throws auth/network-request-failed and no request is sent, so it is the same
  // lost connection and gets the same retry and CloudFunctionNetworkError wrapping
  if (isFirebaseAuthNetworkError(error)) return true;
  // shared transient-connectivity family: fetch failures, aborts, and the
  // iOS + Android native connectivity-loss message shapes
  return isTransientFetchError(error);
}

/** Returns true when the cloud-functions wrapper should retry the call:
 * either a network-class failure (see `isNetworkError`) or a retriable
 * HTTP status (502/503, see `isRetriableHttpError`). Composes the two
 * axes so the four retry-decision sites in `callable()` share one
 * predicate — keeps the network and HTTP arms from drifting if either
 * widens later. */
export function isRetriableError(error: unknown): boolean {
  return isNetworkError(error) || isRetriableHttpError(error);
}

/** Whether `callApiAsync` should report a rejected fetch itself (#6058). Its requests are marked so
 * the global fetch wrapper stays quiet, and `callable()` retries and reports every failure
 * `isRetriableError` recognises. Anything else `callable()` rethrows untouched, so it is reported
 * here -- except on a URL the wrapper never reported either (`flushTelemetry`, whose flusher
 * deliberately reports nothing). */
export function shouldReportUnrecognisedFetchFailure(error: unknown, url: string): boolean {
  return !isRetriableError(error) && !isThirdPartyOrSelfHealingUrl(url);
}

// the raw fetch rejections callApiAsync has already reported (or logged as offline). callable()
// rethrows these unchanged, so a caller cannot tell them from an error nobody reported without this
const fetchFailuresHandledByCallApi = new WeakSet<object>();

/** Records that `callApiAsync` filed the one report for this fetch rejection (#6233). */
export function markFetchFailureHandled(error: unknown): void {
  if (typeof error === 'object' && error !== null) fetchFailuresHandledByCallApi.add(error);
}

/** Whether a cloud-function rejection was already reported, or deliberately left unreported because
 * the device was offline, by the cloud-functions layer. A caller that gets `true` should only log
 * it; anything else reached the caller with nobody having reported it (#6233). */
export function wasReportedByCloudFunctions(error: unknown): boolean {
  // if the retries ran out on a network failure, callable() reported it and wrapped it
  if (error instanceof CloudFunctionNetworkError) return true;
  // if a 502/503 survived its retry, callable() reported it before rethrowing it
  if (isRetriableHttpError(error)) return true;
  // if the fetch itself rejected with something unrecognised, callApiAsync reported it
  return typeof error === 'object' && error !== null && fetchFailuresHandledByCallApi.has(error);
}
